import { AlertTriangle, CheckCircle2, Loader2, Printer, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { useBoxTopperAction } from "@/hooks/useBoxTopper";
import { TRACE_SEAL_INDEX, canRetry, groupSealsByOrder, type SealRow } from "@/utils/boxTopper";

const timeFormat = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Los_Angeles",
  month: "short",
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function StatusPill({ seal }: { seal: SealRow }) {
  if (seal.status === "printed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-status-success/15 px-2 py-0.5 text-xs font-medium text-status-success">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
        Printed
      </span>
    );
  }
  if (seal.status === "failed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-status-danger/15 px-2 py-0.5 text-xs font-medium text-status-danger">
        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
        Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
      Printing
    </span>
  );
}

interface RecentSealsProps {
  seals: SealRow[];
  isLoading: boolean;
}

/**
 * Recent seals, one block per order, with the per-order Reprint button and a
 * Retry on each failed seal. Failures lead the section as a count and keep
 * their error text in view, never behind a hover.
 */
export function RecentSeals({ seals, isLoading }: RecentSealsProps) {
  const action = useBoxTopperAction();
  const { toast } = useToast();
  const groups = groupSealsByOrder(seals);
  const failedCount = seals.filter((s) => s.status === "failed").length;
  const busyKey = action.isPending ? JSON.stringify(action.variables) : null;

  const run = (body: Parameters<typeof action.mutate>[0], label: string) =>
    action.mutate(body, {
      onSuccess: (result) => {
        const failed = result.seals.filter((s) => s.status === "failed");
        if (result.seals.length === 0) {
          toast({ title: `${label}: nothing to print`, description: "Its seals are already printing or were just retried." });
          return;
        }
        toast(
          failed.length
            ? { title: `${label} did not print`, description: failed[0].error ?? "The printer reported a problem.", variant: "destructive" }
            : { title: `${label} sent to the printer` },
        );
      },
      onError: (e) => toast({ title: `${label} failed`, description: e.message, variant: "destructive" }),
    });

  if (isLoading) return <p className="text-sm text-muted-foreground">Loading recent seals</p>;

  if (groups.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted px-4 py-8 text-center">
        <Printer className="mx-auto h-6 w-6 text-muted-foreground" aria-hidden />
        <p className="mt-2 text-sm font-medium text-foreground">No seals yet</p>
        <p className="mt-1 text-xs text-muted-foreground">Once Box Topper is on, each new order's seals show up here as they print.</p>
      </div>
    );
  }

  const now = new Date();
  return (
    <div className="space-y-3">
      {failedCount > 0 && (
        <p role="status" className="flex items-center gap-2 rounded-lg bg-status-danger/10 px-3 py-2 text-sm font-medium text-status-danger">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
          {failedCount === 1 ? "1 seal did not print." : `${failedCount} seals did not print.`} Fix the printer, then tap Retry.
        </p>
      )}
      <ul className="divide-y divide-border rounded-lg border border-border bg-card">
        {groups.map((group) => {
          const reprintKey = JSON.stringify({ action: "reprint", order_id: group.order_id });
          return (
            <li key={group.order_id} className="p-3 sm:p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-sm font-semibold tabular-nums text-foreground">Order {group.order_no ?? group.order_id}</p>
                  <p className="text-xs tabular-nums text-muted-foreground">
                    {timeFormat.format(new Date(group.created_at))}
                    {group.seals.length > 1 ? ` · ${group.seals.length} seals` : ""}
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-11 sm:h-9"
                  disabled={action.isPending}
                  onClick={() => run({ action: "reprint", order_id: group.order_id }, `Order ${group.order_no ?? group.order_id}`)}
                >
                  {busyKey === reprintKey ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : <Printer className="mr-2 h-4 w-4" aria-hidden />}
                  Reprint
                </Button>
              </div>
              <ul className="mt-2 space-y-2">
                {group.seals.map((seal) => {
                  const retryKey = JSON.stringify({ action: "retry", seal_id: seal.id });
                  return (
                    <li key={seal.id} className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex min-w-0 flex-wrap items-center gap-2">
                        <StatusPill seal={seal} />
                        <span className="text-xs tabular-nums text-muted-foreground">
                          {seal.seal_index === TRACE_SEAL_INDEX ? "Printing did not start" : `Seal ${seal.seal_index} of ${seal.seal_count}`}
                          {seal.item_label ? ` · ${seal.item_label}` : ""}
                          {seal.reprint_count > 0 ? ` · reprinted ${seal.reprint_count}x` : ""}
                        </span>
                      </div>
                      {canRetry(seal, now) && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="h-11 border-status-danger/40 text-status-danger hover:bg-status-danger/10 hover:text-status-danger sm:h-9"
                          disabled={action.isPending}
                          onClick={() =>
                            run(
                              { action: "retry", seal_id: seal.id },
                              seal.seal_index === TRACE_SEAL_INDEX ? `Order ${group.order_no ?? group.order_id}` : `Seal ${seal.seal_index}`,
                            )
                          }
                        >
                          {busyKey === retryKey ? <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden /> : <RotateCcw className="mr-2 h-4 w-4" aria-hidden />}
                          Retry
                        </Button>
                      )}
                      {seal.status === "failed" && seal.error && (
                        <p className="w-full break-words text-xs text-status-danger">{seal.error}</p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
